import { OktonSearchSelect } from '@okton/platform-ui';
import { Box, Flex, Grid, useColorModeValue } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import {
  useCentrosCusto,
  useDepartamentos,
  useMoedas,
} from '../../../services/hooks/useCadastros';
import {
  ContaBancaria,
  useVinculosDaConta,
} from '../../../services/hooks/useContasBancarias';

export interface ValoresFiltro {
  conta: string;
  moeda: string;
  centrocusto: string;
  departamento: string;
}

interface FiltersBarProps {
  contas: ContaBancaria[];
  valores: ValoresFiltro;
  onChange: (valores: ValoresFiltro) => void;
}

const FiltersBar: React.FC<FiltersBarProps> = ({
  contas,
  valores,
  onChange,
}) => {
  const bg = useColorModeValue('white', 'gray.800');
  const { data: moedas = [] } = useMoedas();
  const { data: centrosCusto = [] } = useCentrosCusto();
  const { data: departamentos = [] } = useDepartamentos();

  const altera = (campo: keyof ValoresFiltro, valor: string) =>
    onChange({ ...valores, [campo]: valor });

  const opcoesContas = useMemo(
    () => contas.map(c => ({ value: c.id_cts, label: c.descricao_cts })),
    [contas],
  );

  const { data: vinculosDaConta = [] } = useVinculosDaConta(
    valores.conta || undefined,
  );

  // Só Moeda que liquida numa Conta filtra alguma coisa aqui: é por ela que o
  // back chega ao extrato. Com uma Conta escolhida, só as dela.
  const opcoesMoedas = useMemo(
    () =>
      valores.conta
        ? vinculosDaConta.map(v => ({ value: v.moeda_ctm, label: v.nome_moe }))
        : moedas
            .filter(m => !!m.descricao_cts)
            .map(m => ({ value: m.id_moe, label: m.nome_moe })),
    [moedas, vinculosDaConta, valores.conta],
  );

  const opcoesCentros = useMemo(
    () => centrosCusto.map(c => ({ value: c.id_cdc, label: c.nome_cdc })),
    [centrosCusto],
  );
  const opcoesDepartamentos = useMemo(
    () => departamentos.map(d => ({ value: d.id_dpt, label: d.nome_dpt })),
    [departamentos],
  );

  // Mesma moldura e mesmo lugar do seletor de Conta da Conciliação: à
  // direita, abaixo do cabeçalho onde fica o período. Grade de largura fixa
  // porque o ComboBox ocupa todo o espaço que recebe: os quatro numa linha
  // só onde cabem, dois a dois abaixo disso - quebrar um sozinho deixava a
  // moldura larga e vazia à esquerda. Centro de Custo e Departamento são
  // mais largos para o texto de "todos" caber inteiro.
  return (
    <Flex justify="flex-end" mt="4">
      <Grid
        borderWidth="1px"
        p="2"
        pb="1"
        rounded="2xl"
        bg={bg}
        gap="2"
        alignItems="center"
        maxW="100%"
        templateColumns={[
          '1fr',
          null,
          'repeat(2, 270px)',
          null,
          null,
          '220px 220px 270px 270px',
        ]}
      >
        <Box>
          <OktonSearchSelect
            name="dashboard-conta"
            placeholder="Todas as contas"
            options={opcoesContas}
            value={valores.conta}
            onChange={valor =>
              // A Moeda escolhida pode não ser da Conta nova.
              onChange({ ...valores, conta: valor, moeda: '' })
            }
          />
        </Box>
        <Box>
          <OktonSearchSelect
            name="dashboard-moeda"
            placeholder="Todas as moedas"
            options={opcoesMoedas}
            value={valores.moeda}
            emptyMessage="Nenhuma Moeda vinculada"
            onChange={valor => altera('moeda', valor)}
          />
        </Box>
        {/* Centro de Custo e Departamento só existem no título: o extrato
            não tem essas colunas, então alcançam os títulos pendentes, o
            ranking de movimentações e as últimas conciliações. */}
        <Box>
          <OktonSearchSelect
            name="dashboard-centrocusto"
            placeholder="Todos os centros de custo"
            options={opcoesCentros}
            value={valores.centrocusto}
            onChange={valor => altera('centrocusto', valor)}
          />
        </Box>
        <Box>
          <OktonSearchSelect
            name="dashboard-departamento"
            placeholder="Todos os departamentos"
            options={opcoesDepartamentos}
            value={valores.departamento}
            onChange={valor => altera('departamento', valor)}
          />
        </Box>
      </Grid>
    </Flex>
  );
};

export default FiltersBar;
